/* global Office */

import { safeParseJSON } from "./utils/safeParseJSON";

export async function insertEmailText(text: string) {
  // Write text to the cursor point in the compose surface.
  try {
    await Office.context.mailbox.item?.body.setSelectedDataAsync(
      text,
      { coercionType: Office.CoercionType.Text },
      (asyncResult: Office.AsyncResult<void>) => {
        if (asyncResult.status === Office.AsyncResultStatus.Failed) {
          throw asyncResult.error.message;
        }
      }
    );
  } catch (error) {
    console.log("Error: " + error);
  }
}

export function onMessageSendHandler(event) {
  Office.context.mailbox.item?.body.getAsync("text", { asyncContext: event }, () => {
    console.log("MESSAGE SENT");
  });
}

class AttachmentsSessionStorage {
  private ATTACHMENT_KEY = "attelas-receivable-attachments";

  private get apiSupported() {
    return Office.context.mailbox.item?.getAttachmentsAsync !== undefined;
  }

  async get() {
    try {
      if (this.apiSupported) {
        const result = await toAsync(Office.context.mailbox.item?.getAttachmentsAsync)();
        return result?.value.map((attachment) => attachment.id) ?? [];
      } else {
        return safeParseJSON<string[]>(sessionStorage.getItem(this.ATTACHMENT_KEY), []);
      }
    } catch (e) {
      console.error(e);
      return [];
    }
  }

  set(attachmentIds: string[]) {
    if (this.apiSupported) return;
    sessionStorage.setItem(this.ATTACHMENT_KEY, JSON.stringify(attachmentIds));
  }

  remove() {
    if (this.apiSupported) return;
    sessionStorage.removeItem(this.ATTACHMENT_KEY);
  }
}

const attachmentsSessionStorage = new AttachmentsSessionStorage();

export async function createEmail(email: {
  to: string[];
  cc?: string[];
  title: string;
  text: string;
  files: { base64File: string; fileName: string }[];
  isRewrite: boolean;
  font?: string | null;
}) {
  const { to, cc = [], title, text, files, isRewrite, font } = email;
  const emailBody = font ? `<div style="font-family: ${font}">${text}</div>` : text;

  try {
    if (Office.context.mailbox.item?.displayReplyForm != undefined) {
      // TODO: when reading an email, what should we do?
      return;
    }

    if (isRewrite) {
      return toAsync(Office.context.mailbox.item?.body.setAsync)(emailBody, {
        coercionType: Office.CoercionType.Html,
      }).catch((e) => {
        console.error(e);
      });
    }

    // Remove any previously attached attachment
    const attachments = await attachmentsSessionStorage.get();
    if (attachments.length) {
      await Promise.all(
        attachments.map((attachmentId) =>
          toAsync(Office.context.mailbox.item?.removeAttachmentAsync)(attachmentId).catch((e) => {
            console.error(e);
          })
        )
      );
      attachmentsSessionStorage.remove();
    }

    await Promise.all([
      toAsync(Office.context.mailbox.item?.to.setAsync)(to).catch((e) => {
        console.error(e);
      }),
      toAsync(Office.context.mailbox.item?.cc.setAsync)(cc).catch((e) => {
        console.error(e);
      }),
      toAsync(Office.context.mailbox.item?.subject.setAsync)(title).catch((e) => {
        console.error(e);
      }),
      toAsync(Office.context.mailbox.item?.body.setAsync)(emailBody, { coercionType: Office.CoercionType.Html }).catch(
        (e) => {
          console.error(e);
        }
      ),
    ]);

    const results = await Promise.all(
      files.map((file) =>
        toAsync(Office.context.mailbox.item?.addFileAttachmentFromBase64Async)(file.base64File, file.fileName, {
          isInline: false,
        }).catch((e) => {
          console.error(e);
        })
      )
    );
    attachmentsSessionStorage.set(results.map((result) => result?.value).filter(Boolean) as string[]);
  } catch (error) {
    console.log("Error: ", error);
  }
}

function getRecipientEmails(recipients: Office.Recipients): Promise<string[]> {
  return new Promise((resolve) => {
    recipients.getAsync((result) => {
      if (result.status === Office.AsyncResultStatus.Succeeded) {
        resolve(result.value?.map((recipient) => recipient.emailAddress) ?? []);
      } else {
        resolve([]);
      }
    });
  });
}

/** Appends emails to CC, skipping any already on To or CC. */
export async function addEmailsToCc(emails: string[]): Promise<string[]> {
  if (!emails.length || Office.context.mailbox.item?.displayReplyForm != undefined) {
    return [];
  }

  const ccField = Office.context.mailbox.item?.cc;
  const toField = Office.context.mailbox.item?.to;
  if (!ccField || !toField) return [];

  const [currentTo, currentCc] = await Promise.all([getRecipientEmails(toField), getRecipientEmails(ccField)]);
  const existing = new Set([...currentTo, ...currentCc].map((email) => email.toLowerCase()));
  const toAdd = emails.filter((email) => !existing.has(email.toLowerCase()));

  if (!toAdd.length) return [];

  await toAsync(ccField.setAsync)([...currentCc, ...toAdd]).catch((e) => {
    console.error(e);
  });

  return toAdd;
}

export async function replyEmail(
  emailContent: string,
  attachments?: { name: string; content: string | null; link: string | null }[],
  font?: string | null,
  isReplyAll?: boolean
) {
  const emailBody = font ? `<div style="font-family: ${font}">${emailContent}</div>` : emailContent;

  try {
    if (Office.context.mailbox.item?.displayReplyForm != undefined) {
      const replyOptions = {
        htmlBody: emailBody,
        attachments: attachments?.map((attachment) =>
          attachment.link
            ? {
                type: Office.MailboxEnums.AttachmentType.File,
                name: attachment.name,
                url: attachment.link,
              }
            : {
                type: "base64" as Office.MailboxEnums.AttachmentType,
                name: attachment.name,
                base64file: attachment.content,
              }
        ),
      };

      if (isReplyAll && (Office.context.mailbox.item as any)?.displayReplyAllFormAsync) {
        await toAsync((Office.context.mailbox.item as any).displayReplyAllFormAsync)(replyOptions as any);
      } else {
        await toAsync(Office.context.mailbox.item?.displayReplyFormAsync)(replyOptions as any);
      }
    } else {
      await toAsync(Office.context.mailbox.item?.body.setAsync)(emailBody, {
        coercionType: Office.CoercionType.Html,
      });

      // Remove any previously attached attachment
      const oldAttachments = await attachmentsSessionStorage.get();
      if (oldAttachments.length) {
        await Promise.all(
          oldAttachments.map((attachmentId) =>
            toAsync(Office.context.mailbox.item?.removeAttachmentAsync)(attachmentId).catch((e) => {
              console.error(e);
            })
          )
        );
        attachmentsSessionStorage.remove();
      }
      const newAttachments = attachments?.filter((attachment) => attachment.content);
      if (newAttachments?.length) {
        const results = await Promise.all(
          newAttachments.map((file) =>
            toAsync(Office.context.mailbox.item?.addFileAttachmentFromBase64Async)(file.content!, file.name, {
              isInline: false,
            }).catch((e) => {
              console.error(e);
            })
          )
        );
        attachmentsSessionStorage.set(results.map((result) => result?.value).filter(Boolean) as string[]);
      }
    }
  } catch (e) {
    console.warn("Error in replyEmail:", e);
    // If displayReplyFormAsync failed, maybe try without body? Or alert user.
    if (Office.context.mailbox.item?.displayReplyForm != undefined) {
      console.log("Attempting to recover displayReplyForm");
      const recoveryBody = font ? `<div style="font-family: ${font}">${emailContent}</div>` : emailContent;

      if (isReplyAll && (Office.context.mailbox.item as any)?.displayReplyAllFormAsync) {
        await toAsync((Office.context.mailbox.item as any).displayReplyAllFormAsync)({
          htmlBody: recoveryBody,
        } as any).catch((err) => console.error("Retry reply-all failed", err));
      } else {
        await toAsync(Office.context.mailbox.item?.displayReplyFormAsync)({
          htmlBody: recoveryBody,
        }).catch((err) => console.error("Retry reply failed", err));
      }
    }
  }
}

type OverloadedOfficeApi<Param1 extends unknown[], Param2 extends unknown[], Result> = {
  (...args: [...Param1, (result: Office.AsyncResult<Result>) => void]): void;
  (...args: [...Param2, (result: Office.AsyncResult<Result>) => void]): void;
};

/**
 * Turns a "async" office api to real async function
 */
function toAsync<Param1 extends unknown[], Param2 extends unknown[], Result>(
  officeAsyncApi?: OverloadedOfficeApi<Param1, Param2, Result>
) {
  return async (...params: Param1 | Param2) => {
    if (!officeAsyncApi) return;

    return new Promise<Office.AsyncResult<Result>>((resolve, reject) => {
      (officeAsyncApi as any)(...params, (result: Office.AsyncResult<Result>) => {
        if (result.status === Office.AsyncResultStatus.Failed) {
          reject(result.error);
        } else {
          resolve(result);
        }
      });
    });
  };
}

export async function createContactUsEmail(organisationName, userName) {
  try {
    if (Office.context.mailbox.item?.displayReplyForm != undefined) {
      // TODO: when reading an email, what should we do?
      return;
    }

    Office.context.mailbox.item?.to.setAsync(["lei@attelas.ai"], (asyncResult: Office.AsyncResult<void>) => {
      if (asyncResult.status === Office.AsyncResultStatus.Failed) {
        throw asyncResult.error.message;
      }
    });

    Office.context.mailbox.item?.subject.setAsync(
      `${organisationName} would like to know more about Attelas`,
      (asyncResult: Office.AsyncResult<void>) => {
        if (asyncResult.status === Office.AsyncResultStatus.Failed) {
          throw asyncResult.error.message;
        }
      }
    );

    Office.context.mailbox.item?.body.setAsync(
      "Hello,<br />" +
        "I am " +
        userName +
        " from " +
        organisationName +
        ".<br />" +
        "We would like to know more about Attelas and how it can help us.<br />" +
        "Could you please provide us with more information?<br /><br />" +
        "Thank you,<br />" +
        userName,
      { coercionType: Office.CoercionType.Html },
      (asyncResult: Office.AsyncResult<void>) => {
        if (asyncResult.status === Office.AsyncResultStatus.Failed) {
          throw asyncResult.error.message;
        }
      }
    );
  } catch (error) {
    console.log("Error: " + error);
  }
}

export const isMailboxRequirementSupported = (version: string) => {
  try {
    return Office.context.requirements.isSetSupported("Mailbox", version);
  } catch (e) {
    console.error("Error checking Mailbox requirement support:", e);
    return false;
  }
};

export const getPaneMode = () => {
  if (Office.context.mailbox.item?.displayReplyForm != undefined) {
    return "read";
  }
  return "compose";
};

export const getReadingSubject = () => {
  if (Office.context.mailbox.item?.displayReplyForm != undefined) {
    return Office.context.mailbox.item.subject.toString();
  }
  return null;
};

export const getReadingAttachments = () => {
  if (Office.context.mailbox.item?.displayReplyForm != undefined) {
    return Office.context.mailbox.item.attachments;
  }
  return null;
};

export const convertAttachmentIdToRestId = (attachmentId: string) => {
  return Office.context.mailbox.convertToRestId(attachmentId, Office.MailboxEnums.RestVersion.v2_0);
};

export const getReadingEmailAddress = () => {
  if (Office.context.mailbox.item?.displayReplyForm != undefined) {
    return Office.context.mailbox.item.from.emailAddress;
  }
  return null;
};

export const getReadingEmailSenderDisplayName = () => {
  if (Office.context.mailbox.item?.displayReplyForm != undefined) {
    return Office.context.mailbox.item.sender.displayName;
  }
  return "";
};

export const getReadingEmailBody = async () => {
  if (Office.context.mailbox.item?.displayReplyForm != undefined) {
    return new Promise<string>((resolve) => {
      Office.context.mailbox.item?.body.getAsync("text", (result: Office.AsyncResult<string>) => {
        resolve(result.value);
      });
    });
  }
  return "";
};

export const subscribeItemChanged = (callback: () => void) => {
  Office.context.mailbox.addHandlerAsync(Office.EventType.ItemChanged, callback);
  return () => Office.context.mailbox.removeHandlerAsync(Office.EventType.ItemChanged, callback);
};

export function getPlatform() {
  return Office.context.platform;
}

export async function openDialog(url: string) {
  const result = await toAsync(Office.context.ui.displayDialogAsync)(url, { width: 40, height: 70 });
  return result?.value;
}

export function messageParent(message: string) {
  return Office.context.ui.messageParent(message);
}

export const getReadingEmailRestId = () => {
  if (Office.context.mailbox.item?.displayReplyForm != undefined) {
    return Office.context.mailbox.convertToRestId(
      Office.context.mailbox.item.itemId,
      Office.MailboxEnums.RestVersion.v2_0
    );
  }
  return null;
};

export async function openEmailByRestId(emailRestId: string) {
  try {
    const emailId = Office.context.mailbox.convertToEwsId(emailRestId, Office.MailboxEnums.RestVersion.v2_0);
    await toAsync(Office.context.mailbox.displayMessageFormAsync)(emailId);
  } catch (e) {
    console.error(e);
  }
}

export async function getSharedPropertiesAsync() {
  const mode = getPaneMode();
  if (mode === "compose") return;
  try {
    const result = await toAsync(Office.context.mailbox.item?.getSharedPropertiesAsync)();
    return result;
  } catch (e) {
    console.error(e);
    return;
  }
}

export const getCurrentMailboxAddress = async (): Promise<string> => {
  if (Office.context.mailbox.item?.getSharedPropertiesAsync) {
    try {
      const result = await toAsync(Office.context.mailbox.item?.getSharedPropertiesAsync)();
      if (result?.status === Office.AsyncResultStatus.Succeeded) {
        const { owner, targetMailbox } = result.value;
        return owner || targetMailbox;
      } else {
        // not in a shared context — fallback to the signed-in user
        return Office.context.mailbox.userProfile.emailAddress;
      }
    } catch (e) {
      console.error("Error getting shared properties:", e);
      // Fallback to user profile email on error
      return Office.context.mailbox.userProfile.emailAddress;
    }
  }

  return Office.context.mailbox.userProfile.emailAddress;
};
